import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Modal } from 'components/Modal/Modal';
import './ImageCropModal.scss';

/**
 * ImageCropModal Component
 *
 * Modal for cropping images with preview before upload.
 * Uses react-image-crop for cropping functionality.
 *
 * @param {Object} props
 * @param {string} props.imageSrc - Source image URL/base64
 * @param {Function} props.onCropComplete - Callback with cropped image (blob, url)
 * @param {Function} props.onCancel - Cancel callback
 * @param {string} props.aspectRatio - Aspect ratio (default: 1 for square)
 * @param {string} props.title - Modal title
 *
 * @example
 * <ImageCropModal
 *   imageSrc={imageUrl}
 *   onCropComplete={(blob, url) => handleCrop(blob, url)}
 *   onCancel={() => setShowCrop(false)}
 *   aspectRatio={1}
 *   title="Crop Profile Picture"
 * />
 */
export const ImageCropModal = ({
  imageSrc,
  onCropComplete,
  onCancel,
  aspectRatio = 1,
  title = 'Crop Image',
}) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [processing, setProcessing] = useState(false);
  const imgRef = useRef(null);

  /**
   * Handle image load - Initialize crop after image loads
   */
  const onImageLoad = useCallback(
    (e) => {
      const { width, height } = e.currentTarget;
      imgRef.current = e.currentTarget;

      // Set initial crop to center 90% of image
      const cropWidth = Math.min(width, height) * 0.9;
      const cropHeight = cropWidth / aspectRatio;
      const x = (width - cropWidth) / 2;
      const y = (height - cropHeight) / 2;

      const initialCrop = {
        unit: 'px',
        width: cropWidth,
        height: cropHeight,
        x: x,
        y: y,
      };

      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    },
    [aspectRatio]
  );

  /**
   * Generate cropped image blob
   */
  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    // Set canvas size to crop size
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    // Draw cropped image
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          const croppedImageUrl = URL.createObjectURL(blob);
          resolve({ blob, url: croppedImageUrl });
        },
        'image/jpeg',
        0.95
      );
    });
  }, [completedCrop]);

  /**
   * Handle crop and upload
   */
  const handleCrop = async () => {
    setProcessing(true);
    try {
      const result = await getCroppedImg();
      if (result) {
        onCropComplete(result.blob, result.url);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Render modal using portal to ensure it's at document root level
  return ReactDOM.createPortal(
    <Modal
      isOpen={true}
      onClose={onCancel}
      onConfirm={handleCrop}
      title={title}
      confirmText={processing ? 'Processing...' : 'Apply & Upload'}
      closeText='Cancel'
      showConfirmButton={true}
      showCloseButton={true}
      showFooter={true}
      width='700px'
      height='auto'
    >
      <div className='image-crop-content'>
        <div className='crop-container'>
          {crop && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={aspectRatio === 1}
            >
              <img
                src={imageSrc}
                alt='Crop preview'
                onLoad={onImageLoad}
                style={{ maxWidth: '100%', maxHeight: '500px' }}
              />
            </ReactCrop>
          )}
          {!crop && (
            <img
              src={imageSrc}
              alt='Loading'
              onLoad={onImageLoad}
              style={{ maxWidth: '100%', maxHeight: '500px' }}
            />
          )}
        </div>

        <div className='crop-instructions'>
          <p>📌 Drag to reposition • Resize corners to adjust</p>
        </div>
      </div>
    </Modal>,
    document.body
  );
};
